import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import axios from 'axios';
import type { JSX } from 'react';
import React, { useContext, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

import { BillingReportContext } from '../../contexts/BillingReportProvider';
import BillingItemEditRepository from '../../repositories/BillingItemEditRepository';
import { titleize } from '../../utils/titleize';
import RowEditorField from '../RowEditorField';

import { BillingItemEdit, FieldEdit, FieldEditTypeSlugs, FieldEditTypes, Props } from './types';

function createFieldEdit(): FieldEdit {
  return {
    id: uuidv4(),
    edit_type: FieldEditTypes[0].slug,
    value: ''
  };
}

const RowEditor = ({ rows, onDismiss, afterSave }: Props): JSX.Element => {
  const { setMessages } = useContext(BillingReportContext);
  const [loading, setLoading] = useState(false);
  const [notes, setNotes] = useState('');
  const [fieldEdits, setFieldEdits] = useState<FieldEdit[]>([createFieldEdit()]);

  const large = rows.length > 100;
  const repository = BillingItemEditRepository();

  const undoEdit = async (editId: number) => {
    console.log('Undoing edit', editId);

    try {
      setLoading(true);
      await repository.deleteBillingItemGroup(editId);
      setMessages([
        {
          message: 'Reverted Edit. An additional refresh of affected rows may be required.',
          type: 'success'
        }
      ]);
    } catch (error) {
      setMessages([{ message: 'Error undoing billing item edit', type: 'error' }]);
      console.log('Error undoing billing item edit', error);
    } finally {
      setLoading(false);
    }
  };

  const clearEdits = async () => {
    const billingItemIds = rows.map((row) => row.billing_item_id);
    try {
      setLoading(true);
      await repository.clearBillingItemEdits(billingItemIds);
      setMessages([
        {
          message: 'Cleared Edits for selected Items',
          type: 'success'
        }
      ]);

      afterSave({ refresh: !large });
    } catch (error) {
      setMessages([{ message: 'Error clearing billing item edits', type: 'error' }]);
      console.log('Error clearing billing item edits', error);
    }
  };

  const onSave = async () => {
    const billingItemIds = rows.map((row) => row.billing_item_id);

    const edit: BillingItemEdit = {
      field_edits: fieldEdits.map((fieldEdit) => {
        // Check value for "," and split into array
        if (
          [
            FieldEditTypeSlugs.add_modifiers,
            FieldEditTypeSlugs.remove_modifiers,
            FieldEditTypeSlugs.set_modifiers
          ].includes(fieldEdit.edit_type)
        ) {
          fieldEdit.value = fieldEdit.value.toString().split(',').filter(Boolean);
        }
        return fieldEdit;
      }),
      notes,
      billing_item_ids: billingItemIds
    };

    try {
      setLoading(true);
      const group = await repository.createBillingItemEdit(edit);
      setMessages([
        {
          message: 'Saved Edit',
          type: 'success',
          actionText: 'Undo',
          onAction: () => undoEdit(group.id)
        },
        {
          message: large
            ? 'Over 100 rows affected. Auto-refresh disabled.'
            : 'Affected rows are being refreshed.',
          type: 'info'
        }
      ]);

      afterSave({ refresh: !large });
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const errorMessages: Record<string, string[]> = error.response?.data;
        if (errorMessages) {
          setMessages(
            Object.entries(errorMessages).map(([key, value]) => ({
              message: `${titleize(key)}: ${value.join(', ')}`,
              type: 'error'
            }))
          );
        } else {
          setMessages([{ message: 'Error saving billing item edit', type: 'error' }]);
        }
      } else {
        setMessages([{ message: 'Error saving billing item edit group', type: 'error' }]);
      }

      console.log('Error saving billing item edit', error);
    } finally {
      setLoading(false);
    }
  };

  const onAddFieldEdit = () => {
    // Add a new field edit
    setFieldEdits([...fieldEdits, createFieldEdit()]);
  };

  const onRemoveFieldEdit = (id: string) => {
    // Remove the field edit
    setFieldEdits(fieldEdits.filter((fieldEdit) => fieldEdit.id !== id));
  };

  return (
    <div className="my-2 max-w-5xl bg-white p-6 rounded shadow">
      <h2 className="text-xl font-bold">Add Bulk Edits</h2>

      {/* Field Edits */}
      <div className="mt-4">
        <h3 className="text-lg font-bold">Field Edits</h3>
        <div className="mt-4">
          {fieldEdits.map((fieldEdit, index) => (
            <RowEditorField
              key={fieldEdit.id}
              fieldEdit={fieldEdit}
              onRemove={() => onRemoveFieldEdit(fieldEdit.id)}
              onChange={(newFieldEdit) => {
                const newFieldEdits = [...fieldEdits];
                newFieldEdits[index] = newFieldEdit;
                setFieldEdits(newFieldEdits);
              }}
            />
          ))}
        </div>
      </div>

      {/* Top Level Edit Information */}

      <div className="mt-4">
        {/* Notes */}
        <div className="mt-4">
          <label htmlFor="notes" className="">
            * Notes:
          </label>
          <textarea
            id="notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            className="p-3 w-full text-sm text-gray-700 border border-gray-300 rounded focus:outline-none focus:border-blue-500 transition duration-200 ease-in-out resize-none"
          />
        </div>
      </div>

      <div className="mt-4">
        <div className="mt-4 flex justify-between">
          {/* Clear any existing Edits */}
          <div>
            <button onClick={onDismiss} className="bg-gray-400 text-white px-2 py-2 rounded mr-2">
              Close
            </button>

            <button
              className="bg-red-600 text-white px-2 py-2 rounded"
              onClick={() => clearEdits()}>
              Clear Edits
            </button>
          </div>

          {/* Add another field edit */}
          <div>
            <button
              className="bg-blue-600 text-white px-2 py-2 rounded"
              onClick={() => onAddFieldEdit()}>
              Add Field Edit
            </button>
          </div>

          {/* Save */}
          <div>
            <button className="bg-green-600 text-white px-2 py-2 rounded" onClick={() => onSave()}>
              {loading ? (
                <>
                  <FontAwesomeIcon icon={faSpinner} className="mr-2 animate-spin" />
                  Saving
                </>
              ) : (
                'Save'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RowEditor;
